import {
  AccountingPeriodLock,
  IAccountingPeriodLock,
  ICreateAccountingPeriodLockRequest,
  IRevenueRecognitionResponse,
  IRevenueSchedulePeriod,
  IRevenueSchedulePeriodSearchParams,
  IRevenueScheduleSearchParams,
  IRevenueScheduleSubscriptionItem,
  IRevenueScheduleSubscriptionItemSearchParams,
  IRevenueScheduleSummary,
  IRevRecSettingsResponse,
  IRevRecWaterfallRequest,
  RevenueRecognitionResponse,
  RevenueSchedulePeriod,
  RevenueScheduleSubscriptionItem,
  RevenueScheduleSummary,
  RevRecProrationModelEnum,
  RevRecV2RequestPayload,
} from '@monetize/types/app';
import { ApiListResponse } from '@monetize/types/common';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { unparse } from 'papaparse';
import {
  generateRevRecFilename,
  transformRevRecToTabularData,
} from '../routes/RevenueRecognition/rev-rec.utils';
import { apiGet, apiGetAllList, apiPost, apiPut } from './axios';

export const revenueKeys = {
  base: ['revenue'] as const,
  revRecSettings: () => [...revenueKeys.base, 'settings'] as const,
  revRecSchedule: () => [...revenueKeys.base, 'schedule'] as const,
  revenueSchedules: () => [...revenueKeys.base, 'revenueSchedules'] as const,
  revenueSchedulesSearch: (params: IRevenueScheduleSearchParams) =>
    [...revenueKeys.revenueSchedules(), 'search', params] as const,
  revenueScheduleSummary: (scheduleId: string) =>
    [...revenueKeys.revenueSchedules(), 'summary', scheduleId] as const,
  revenueSchedulePeriods: (scheduleId: string) =>
    [...revenueKeys.revenueSchedules(), 'periods', scheduleId] as const,
  revenueSchedulePeriodsRange: (
    scheduleId: string,
    params: IRevenueSchedulePeriodSearchParams,
  ) =>
    [
      ...revenueKeys.revenueSchedulePeriods(scheduleId),
      'range',
      params,
    ] as const,
  revenueScheduleSubscriptionItems: (
    scheduleId: string,
    params?: IRevenueScheduleSubscriptionItemSearchParams,
  ) =>
    [
      ...revenueKeys.revenueSchedules(),
      'subscriptionItems',
      scheduleId,
      params ?? {},
    ] as const,
  activePeriodLock: () => [...revenueKeys.base, 'activePeriodLock'] as const,
  periodLockHistory: () => [...revenueKeys.base, 'periodLockHistory'] as const,
};

export function useRevRecSettings(
  options: Partial<UseQueryOptions<IRevRecSettingsResponse>> = {},
) {
  return useQuery({
    queryKey: [...revenueKeys.revRecSettings()],
    queryFn: () =>
      apiGet<IRevRecSettingsResponse>(
        '/revenue-recognition/configurations/general',
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    select: (data) => ({
      ...data,
      prorationModel: data.enabled
        ? data.prorationModel
        : RevRecProrationModelEnum.DAY_BASED,
    }),
    ...options,
  });
}

export function useSaveRevRecSettings(
  options: Partial<
    UseMutationOptions<void, unknown, IRevRecSettingsResponse>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<void, unknown, IRevRecSettingsResponse>({
    mutationFn: (params) =>
      apiPut('/revenue-recognition/configurations/general', params).then(
        (res) => res.data,
      ),

    onSuccess: (response, variables, context) => {
      queryClient.setQueryData([...revenueKeys.revRecSettings()], variables);
      onSuccess && onSuccess(response, variables, context);
    },
    ...restOptions,
  });
}

export function useRevRecSchedule(
  params: IRevRecWaterfallRequest,
  options: Partial<UseQueryOptions<IRevenueRecognitionResponse>> = {},
) {
  return useQuery({
    queryKey: [...revenueKeys.revRecSchedule(), params],
    queryFn: () =>
      apiGet<IRevenueRecognitionResponse>('/api/revenueRecognition/dashboard', {
        params,
      }).then((res) => RevenueRecognitionResponse.parse(res.data)),

    ...options,
  });
}

export function useExportRevRec(
  options: Partial<
    UseMutationOptions<string, unknown, IRevRecWaterfallRequest>
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  return useMutation<string, unknown, IRevRecWaterfallRequest>({
    mutationFn: (params) =>
      apiGet<IRevenueRecognitionResponse>('/api/revenueRecognition/dashboard', {
        params,
      })
        .then((res) => RevenueRecognitionResponse.parse(res.data))
        .then((revRecData) => {
          const { recognitionSchedule } = revRecData;
          const periods = recognitionSchedule[0]?.periods || [];
          const { rows } = transformRevRecToTabularData(revRecData);
          const csv = unparse(
            {
              fields: [
                'period',
                'currency',
                'total',
                ...periods.map((period) => period.period),
                'deferred',
              ],
              data: rows,
            },
            { header: true },
          );
          return csv;
        }),
    onSuccess: (...response) => {
      onSuccess && onSuccess(...response);
    },
    ...restOptions,
  });
}

export function useDownloadRevenueScheduleV2(
  options: Partial<
    UseMutationOptions<
      { data: ArrayBuffer; filename: string },
      unknown,
      RevRecV2RequestPayload
    >
  > = {},
) {
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiPost(
        '/revenue-recognition/schedules',
        payload,
        {
          responseType: 'arraybuffer',
        },
      );

      return {
        data: response.data,
        filename: generateRevRecFilename('revenue-schedule', payload),
      };
    },
    ...options,
  });
}

export function useSearchRevenueSchedules(
  params: IRevenueScheduleSearchParams,
  options: Partial<
    UseQueryOptions<ApiListResponse<IRevenueScheduleSummary>>
  > = {},
) {
  return useQuery({
    queryKey: revenueKeys.revenueSchedulesSearch(params),
    queryFn: () =>
      apiGet<ApiListResponse<IRevenueScheduleSummary>>(
        '/revenue-recognition/schedules',
        { params },
      ).then((res) => ({
        ...res.data,
        content: res.data.content.map((item) =>
          RevenueScheduleSummary.parse(item),
        ),
      })),
    ...options,
  });
}

export function useGetRevenueScheduleSummary(
  scheduleId: string,
  options: Partial<UseQueryOptions<IRevenueScheduleSummary>> = {},
) {
  return useQuery({
    queryKey: revenueKeys.revenueScheduleSummary(scheduleId),
    queryFn: () =>
      apiGet<IRevenueScheduleSummary>(
        `/revenue-recognition/schedules/${scheduleId}/summary`,
      ).then((res) => RevenueScheduleSummary.parse(res.data)),
    enabled: !!scheduleId,
    ...options,
  });
}

export interface IRevenueSchedulePeriodOverride {
  periodId: string;
  revenue: number;
}

export function useUpdateRevenueScheduleOverrides(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        scheduleId: string;
        periodOverrides: IRevenueSchedulePeriodOverride[];
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation({
    mutationFn: ({ scheduleId, periodOverrides }) =>
      apiPut<void>(`/revenue-recognition/schedules/${scheduleId}/overrides`, {
        periodOverrides,
      }).then((res) => res.data),
    onSuccess: async (response, variables, context) => {
      // Await the periods refetch so callers awaiting mutateAsync don't
      // re-render with stale cached values before the new data lands.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: revenueKeys.revenueSchedulePeriods(variables.scheduleId),
        }),
        queryClient.invalidateQueries({
          queryKey: revenueKeys.revenueSchedules(),
        }),
      ]);
      onSuccess?.(response, variables, context);
    },
    ...restOptions,
  });
}

export function useGetRevenueSchedulePeriods(
  scheduleId: string,
  options: Partial<UseQueryOptions<IRevenueSchedulePeriod[]>> = {},
) {
  return useQuery({
    queryKey: revenueKeys.revenueSchedulePeriods(scheduleId),
    queryFn: async () => {
      const items = await apiGetAllList<IRevenueSchedulePeriod>(
        `/revenue-recognition/schedules/${scheduleId}/periods`,
      );
      return items.map((item) => RevenueSchedulePeriod.parse(item));
    },
    enabled: !!scheduleId,
    ...options,
  });
}

export async function fetchRevenueSchedulePeriodsByRange(
  scheduleId: string,
  params: IRevenueSchedulePeriodSearchParams,
): Promise<IRevenueSchedulePeriod[]> {
  const {
    windowStart,
    windowEnd,
    subscriptionId,
    subscriptionItemId,
    offeringId,
    productId,
    sourceId,
  } = params;

  const filters: Record<string, unknown> = {};
  if (windowEnd !== undefined) {
    filters.periodStart = { between: { lte: windowEnd } };
  }
  if (windowStart !== undefined) {
    filters.periodEnd = { between: { gte: windowStart } };
  }
  if (subscriptionId !== undefined) {
    filters.subscriptionId = subscriptionId;
  }
  if (subscriptionItemId !== undefined) {
    filters.subscriptionItemId = subscriptionItemId;
  }
  if (offeringId !== undefined) {
    filters.offeringId = offeringId;
  }
  if (productId !== undefined) {
    filters.productId = productId;
  }
  if (sourceId !== undefined) {
    filters.sourceId = sourceId;
  }

  const items = await apiGetAllList<IRevenueSchedulePeriod>(
    `/revenue-recognition/schedules/${scheduleId}/periods`,
    { filters },
  );
  return items.map((item) => RevenueSchedulePeriod.parse(item));
}

export function useGetRevenueSchedulePeriodsByRange(
  scheduleId: string,
  params: IRevenueSchedulePeriodSearchParams,
  options: Partial<UseQueryOptions<IRevenueSchedulePeriod[]>> = {},
) {
  return useQuery({
    queryKey: revenueKeys.revenueSchedulePeriodsRange(scheduleId, params),
    queryFn: () => fetchRevenueSchedulePeriodsByRange(scheduleId, params),
    enabled: !!scheduleId,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

export function useGetRevenueScheduleSubscriptionItems(
  scheduleId: string,
  params: IRevenueScheduleSubscriptionItemSearchParams = {},
  options: Partial<UseQueryOptions<IRevenueScheduleSubscriptionItem[]>> = {},
) {
  return useQuery({
    queryKey: revenueKeys.revenueScheduleSubscriptionItems(scheduleId, params),
    queryFn: async () => {
      const items = await apiGetAllList<IRevenueScheduleSubscriptionItem>(
        `/revenue-recognition/schedules/${scheduleId}/subscription-items`,
        { filters: params },
      );
      return items.map((item) => RevenueScheduleSubscriptionItem.parse(item));
    },
    enabled: !!scheduleId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    ...options,
  });
}

const CONTENT_DISPOSITION_FILENAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

const parseFilenameFromContentDisposition = (
  header: string | undefined,
): string | undefined => {
  if (!header) {
    return undefined;
  }
  const match = header.match(CONTENT_DISPOSITION_FILENAME);
  if (!match?.[1]) {
    return undefined;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export function useDownloadLatestRevenueScheduleByContract(
  options: Partial<
    UseMutationOptions<
      { data: ArrayBuffer; filename: string },
      unknown,
      { revenueContractId: string; fallbackId?: string }
    >
  > = {},
) {
  return useMutation({
    mutationFn: async ({ revenueContractId, fallbackId }) => {
      const response = await apiGet<ArrayBuffer>(
        `/revenue-recognition/revenue-contracts/${revenueContractId}/schedules/latest`,
        { responseType: 'arraybuffer' },
      );
      const headerFilename = parseFilenameFromContentDisposition(
        response.headers?.['content-disposition'],
      );
      return {
        data: response.data,
        filename:
          headerFilename ??
          `revenue-schedule-${fallbackId ?? revenueContractId}.csv`,
      };
    },
    ...options,
  });
}

export function useDownloadRevenueJournalV2(
  options: Partial<
    UseMutationOptions<
      { data: ArrayBuffer; filename: string },
      unknown,
      RevRecV2RequestPayload
    >
  > = {},
) {
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiPost(
        '/revenue-recognition/journalEntries',
        payload,
        {
          responseType: 'arraybuffer',
        },
      );

      return {
        data: response.data,
        filename: generateRevRecFilename('revenue-journal', payload),
      };
    },
    ...options,
  });
}

export function useGetActivePeriodLock(
  options: Partial<UseQueryOptions<IAccountingPeriodLock | null>> = {},
) {
  return useQuery({
    queryKey: revenueKeys.activePeriodLock(),
    queryFn: async () => {
      const response = await apiGet<unknown>(
        '/revenue-recognition/accounting-periods/locks/active',
      );
      if (response.data == null || response.data === '') {
        return null;
      }
      return AccountingPeriodLock.parse(response.data);
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useGetAccountingPeriodLockHistory(
  options: Partial<UseQueryOptions<IAccountingPeriodLock[]>> = {},
) {
  return useQuery({
    queryKey: revenueKeys.periodLockHistory(),
    queryFn: async () => {
      const items = await apiGetAllList<IAccountingPeriodLock>(
        '/revenue-recognition/accounting-periods/locks',
      );
      return items
        .map((item) => AccountingPeriodLock.parse(item))
        .sort((itemA, itemB) => {
          const dateA = itemA.persistenceMetadata?.createDate ?? '';
          const dateB = itemB.persistenceMetadata?.createDate ?? '';
          return dateB.localeCompare(dateA);
        });
    },
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useCreateAccountingPeriodLock(
  options: Partial<
    UseMutationOptions<
      IAccountingPeriodLock | null,
      unknown,
      ICreateAccountingPeriodLockRequest
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation({
    mutationFn: async (payload) => {
      const res = await apiPost<unknown>(
        '/revenue-recognition/accounting-periods/locks',
        payload,
      );
      if (res.data == null || res.data === '') {
        return null;
      }
      return AccountingPeriodLock.parse(res.data);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: revenueKeys.activePeriodLock(),
      });
      queryClient.invalidateQueries({
        queryKey: revenueKeys.periodLockHistory(),
      });
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
